import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  Tooltip,
  Typography,
} from '@mui/material';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutlined';
import { api, type AppNotification } from '../lib/api';
import { navigate, paths } from '../lib/router';
import { useAuthStore } from '../store/authStore';
import { timeAgo } from './home/portalStyles';

const POLL_MS = 2 * 60_000;

const ICONS: Record<AppNotification['type'], typeof AlternateEmailIcon> = {
  mention: AlternateEmailIcon,
  announcement: CampaignOutlinedIcon,
  shared: PeopleOutlineIcon,
  editor: EditOutlinedIcon,
};

function destination(n: AppNotification): string {
  if (n.type === 'announcement') return n.deck_id ? paths.view(n.deck_id) : paths.announcements();
  if (n.type === 'editor' && n.deck_id) return paths.edit(n.deck_id);
  return n.deck_id ? paths.view(n.deck_id) : paths.home();
}

/**
 * Bell for @mentions, new announcements, decks shared to your department and edit invites.
 * The unread count is kept on the server, so it's the same on every device.
 */
export function NotificationsBell() {
  const signedIn = useAuthStore((s) => Boolean(s.user));
  const [items, setItems] = useState<AppNotification[] | null>(null);
  const [unread, setUnread] = useState(0);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const load = useCallback(
    () =>
      api
        .notifications()
        .then((r) => {
          setItems(r.notifications);
          setUnread(r.unread);
        })
        .catch(() => setItems((prev) => prev ?? [])),
    [],
  );

  useEffect(() => {
    if (!signedIn) return;
    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [signedIn, load]);

  if (!signedIn) return null;

  const open = (el: HTMLElement) => {
    setAnchor(el);
    if (unread) {
      setUnread(0);
      void api.markNotificationsSeen().catch(() => undefined);
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton aria-label={unread ? `Notifications, ${unread} new` : 'Notifications'} onClick={(e) => open(e.currentTarget)}>
          <Badge color="error" badgeContent={unread} max={9}>
            <NotificationsNoneOutlinedIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: 'calc(100vw - 32px)', maxHeight: 480 } } }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="subtitle2" component="h2">
            Notifications
          </Typography>
          <Typography variant="caption">Mentions, announcements, shared decks and edit invites from the last 30 days</Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        {items === null ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 3 }}>
            <CircularProgress size={20} aria-label="Loading notifications" />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2, textAlign: 'center' }}>
            You're all caught up.
          </Typography>
        ) : (
          items.map((n) => {
            const Icon = ICONS[n.type];
            return (
              <ButtonBase
                key={`${n.type}-${n.id}`}
                onClick={() => {
                  setAnchor(null);
                  navigate(destination(n));
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  width: '100%',
                  textAlign: 'left',
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  bgcolor: n.unread ? 'chrome.tint' : 'transparent',
                  '&:hover': { bgcolor: 'chrome.subtle' },
                }}
              >
                <Icon sx={{ fontSize: 18, mt: 0.25, color: 'primary.main' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: n.unread ? 700 : 500 }}>
                    {n.title}
                  </Typography>
                  {n.body && (
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                      {n.body}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    {timeAgo(n.at)}
                  </Typography>
                </Box>
              </ButtonBase>
            );
          })
        )}
      </Menu>
    </>
  );
}
